'use client'

import { useContext, useState } from 'react'
import { Card, Cell, Icon } from '@vinted/web-ui'
import { VerticalDots16 } from '@vinted/monochrome-icons'
import classNames from 'classnames'
import { v4 as uuid } from 'uuid'

import {
  useConversationContext,
  ConversationListContext,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { setSessionStorageItem } from '@marketplace-web/browser/browser-storage-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { OutsideClick } from '@marketplace-web/common-components/outside-click-ui'
import { urlWithParams } from '@marketplace-web/browser/url-util'
import {
  deleteMessage,
  deleteMessageNew,
  deletePhotoMessage,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { clickEvent, messagingClickEvent } from '@marketplace-web/messaging/tracking-data'
import { REPORT_SESSION_ID_KEY, AlertType } from '@marketplace-web/admin-alert/admin-alert-data'

import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import { NEW_ADMIN_ALERT_URL } from '../../constants/routes'
import DeleteMessageModal from './DeleteMessageModal'

type Props = {
  messageId: number | string
  isCurrentAuthor: boolean
  photoId?: number
  isLast?: boolean
}

const MessageMenu = ({ messageId, photoId, isLast, isCurrentAuthor }: Props) => {
  const translate = useTranslate('conversation.delete_message')
  const { relativeUrl } = useBrowserNavigation()

  const [isMessageMenuVisible, setIsMessageMenuVisible] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const { track } = useTracking()

  const { conversation, getConversationRequest } = useConversationContext()
  const conversationId = conversation?.id
  const oppositeUser = conversation?.oppositeUser
  const isSystemUser = oppositeUser?.isSystem

  const { updateListConversationTitle } = useContext(ConversationListContext)
  const isAuthorOppositeUser =
    !isCurrentAuthor && !oppositeUser?.isSystem && !oppositeUser?.isModerator

  const reportUrl = urlWithParams(NEW_ADMIN_ALERT_URL, {
    ref_id: conversationId,
    ref_type: AlertType.Conversation,
    ref_url: relativeUrl,
    reported_message_id: messageId,
    reported_photo_id: photoId,
  })

  const isWebMessagingSvcFSEnabled = useFeatureSwitch('web_messaging_svc')

  const handleReportClick = () => {
    setSessionStorageItem(REPORT_SESSION_ID_KEY, uuid())

    track(
      clickEvent({
        screen: 'message_list',
        target: 'report_conversation',
        targetDetails: String(conversationId),
      }),
    )

    track(
      messagingClickEvent({
        screen: 'conversation',
        target: 'report_conversation',
        userSide: conversation?.transaction?.userSide,
        conversationId: String(conversationId),
      }),
    )
  }

  const handleMenuIconClick = () => {
    setIsMessageMenuVisible(!isMessageMenuVisible)

    if (isMessageMenuVisible) return

    track(
      messagingClickEvent({
        screen: 'conversation',
        target: 'open_message_menu',
        conversationId: String(conversationId),
        userSide: conversation?.transaction?.userSide,
      }),
    )
  }

  const handleDeleteClick = () => {
    track(
      messagingClickEvent({
        conversationId,
        screen: 'conversation',
        target: 'initiate_delete_message',
        userSide: conversation?.transaction?.userSide,
      }),
    )

    setIsModalVisible(true)
    setIsMessageMenuVisible(false)
  }

  const deleteCurrentMessagePhoto = async () => {
    if (!conversationId || !photoId) return

    if (isWebMessagingSvcFSEnabled) {
      await deleteMessageNew({ conversationId, messageId })

      return
    }

    await deletePhotoMessage({ conversationId, messageId, photoId })
  }

  const deleteCurrentMessage = async () => {
    if (!conversationId) return

    if (isWebMessagingSvcFSEnabled) {
      await deleteMessageNew({ conversationId, messageId })

      return
    }

    await deleteMessage({ conversationId, messageId })
  }

  const handleDeleteConfirm = async () => {
    if (!conversationId) return

    track(
      messagingClickEvent({
        conversationId,
        screen: 'conversation',
        target: 'confirm_delete_message',
        userSide: conversation?.transaction?.userSide,
      }),
    )

    if (photoId) {
      await deleteCurrentMessagePhoto()
    } else {
      await deleteCurrentMessage()
    }

    if (isLast) {
      updateListConversationTitle({
        id: conversationId,
        title: translate('message_deleted'),
      })
    }

    setIsModalVisible(false)
    getConversationRequest({ conversationId })
  }

  const handleOutsideClick = () => {
    setIsMessageMenuVisible(false)
  }

  const handleDeleteCancel = () => {
    setIsModalVisible(false)
  }

  const menuIconClassName = classNames('message-menu__icon', {
    'message-menu--hidden': !isMessageMenuVisible,
  })

  const menuClassName = classNames('message-menu__content', {
    'message-menu--hidden': !isMessageMenuVisible,
  })

  if (!isCurrentAuthor && !isAuthorOppositeUser) return null

  return (
    <div className="message-menu">
      <OutsideClick onOutsideClick={handleOutsideClick}>
        <div className={menuClassName}>
          {isCurrentAuthor && (
            <Card styling="lifted">
              <Cell
                body={translate('actions.delete')}
                clickable
                onClick={handleDeleteClick}
                testId="delete-option"
              />
            </Card>
          )}
          {isAuthorOppositeUser && (
            <Card styling="lifted">
              <Cell
                body={translate('actions.report')}
                url={reportUrl}
                onClick={handleReportClick}
                testId="report-option"
              />
            </Card>
          )}
        </div>
        {!isSystemUser && (
          <span className={menuIconClassName}>
            <Cell testId="menu-icon" styling="tight" clickable onClick={handleMenuIconClick}>
              <Icon name={VerticalDots16} color="greyscale-level-3" />
            </Cell>
          </span>
        )}
      </OutsideClick>
      <DeleteMessageModal
        show={isModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  )
}

export default MessageMenu
